import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Lock, Sparkles } from "lucide-react";
import { LandingFooter } from "@/components/LandingFooter";
import { FeatureCard, type CardTone } from "@/components/guest/FeatureCard";
import { GuestUpgradeModal } from "@/components/GuestUpgradeModal";
import { CreatorNote } from "@/components/CreatorNote";
import { GroundingVideo } from "@/components/explore/GroundingVideo";


import { ExperienceJourney } from "@/components/ExperienceJourney";
import { TestimonialsSection } from "@/components/TestimonialsSection";

import recoveryJournalArt from "@/assets/locked/recovery-journal.png";
import dreamJournalArt from "@/assets/locked/dream-journal.png";
import breathingArt from "@/assets/locked/breathing.png";
import groundingArt from "@/assets/locked/grounding.png";
import somaticArt from "@/assets/locked/somatic.png";
import shadowWorkArt from "@/assets/locked/shadow-work.png";
import sleepArt from "@/assets/locked/sleep.png";
import communityArt from "@/assets/locked/community.png";
import eventsArt from "@/assets/locked/events.png";
import libraryArt from "@/assets/locked/library.png";
import dailyCheckInArt from "@/assets/locked/daily-check-in.png";


const GUEST_OG =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/bda540b3-55c7-4373-af6d-b5588ecb7c64";

export const Route = createFileRoute("/guest")({
  head: () => ({
    meta: [
      { title: "Explore Recovery Trauma™ — Guest Access" },
      {
        name: "description",
        content:
          "Take a guided look around Recovery Trauma™ before joining. Explore trauma-informed tools, a therapist directory, blog, FAQ and more — no account needed.",
      },
      { property: "og:title", content: "Explore Recovery Trauma™ — Guest Access" },
      {
        property: "og:description",
        content:
          "A calm, guided preview of Recovery Trauma™ — explore what Membership unlocks. No account required.",
      },
      { property: "og:url", content: "https://recoverytrauma.com/guest" },
      { property: "og:image", content: GUEST_OG },
      { name: "twitter:image", content: GUEST_OG },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/guest" }],
  }),
  component: GuestPage,
});

type OpenFeature = {
  emoji: string;
  title: string;
  description: string;
  to: string;
  tone: CardTone;
};

const OPEN_FEATURES: OpenFeature[] = [
  {
    emoji: "💖",
    title: "Daily Affirmations",
    description: "Trauma-informed affirmations to soften the day.",
    to: "/affirmations",
    tone: "pink",
  },
  {
    emoji: "🌸",
    title: "Acts of Self-Care",
    description: "Small, gentle rituals that meet you where you are.",
    to: "/acts-of-self-care",
    tone: "peach",
  },
  {
    emoji: "❤️",
    title: "Self-Love Ideas",
    description: "Ways to soften the inner voice and come home to yourself.",
    to: "/self-love",
    tone: "lavender",
  },
  {
    emoji: "💛",
    title: "Find a Therapist",
    description: "Browse our trauma-informed therapist directory.",
    to: "/therapists",
    tone: "sage",
  },
  {
    emoji: "📚",
    title: "Blog & Recovery Articles",
    description: "Long-form writing on recovery, nervous system and safety.",
    to: "/blog",
    tone: "sky",
  },
  {
    emoji: "❓",
    title: "FAQ",
    description: "Everything about the platform, membership and safety.",
    to: "/faq",
    tone: "teal",
  },
];


type LockedFeature = {
  emoji: string;
  title: string;
  description: string;
  art: string;
};

const LOCKED_FEATURES: LockedFeature[] = [
  { emoji: "🌤️", title: "Daily Check-In", description: "A gentle daily moment to notice how you are — mood, energy and needs, kept private to you.", art: dailyCheckInArt },
  { emoji: "📓", title: "Recovery Journal", description: "Private, guided journaling for the recovery journey.", art: recoveryJournalArt },
  { emoji: "🌌", title: "Dream Journal", description: "Capture and gently explore what surfaces at night.", art: dreamJournalArt },
  { emoji: "🌬️", title: "Breathing Exercises", description: "Guided, illustrated breathwork for regulation.", art: breathingArt },

  { emoji: "🌱", title: "Grounding Exercises", description: "Return to the present with sensory practices.", art: groundingArt },
  { emoji: "🪷", title: "Somatic Practices", description: "Body-based practices for stored trauma.", art: somaticArt },
  { emoji: "🌒", title: "Shadow Work", description: "Structured prompts for the parts we exile.", art: shadowWorkArt },
  { emoji: "🌙", title: "Sleep Support", description: "Wind-down rituals and calming sounds.", art: sleepArt },
  { emoji: "💬", title: "Community", description: "A private space with people who understand.", art: communityArt },
  { emoji: "📅", title: "Events", description: "Live circles, workshops and expert sessions.", art: eventsArt },
  { emoji: "📚", title: "Recovery Library", description: "Worksheets, guides and creative reflection.", art: libraryArt },
];

function GuestPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [featureLabel, setFeatureLabel] = useState<string | undefined>(undefined);

  const openLock = (label: string) => {
    setFeatureLabel(label);
    setModalOpen(true);
  };

  return (
    <div className="relative">
      <div
        aria-hidden
        className="guest-wash pointer-events-none absolute inset-x-0 top-0 -z-10 h-[900px] opacity-80"
      />


      <div className="mx-auto max-w-6xl px-4 py-10 sm:py-14">
        {/* Welcome */}
        <header className="mx-auto max-w-3xl text-center animate-fade-in">
          <span className="inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/40 bg-gold-light/40 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--gold-deep)]">
            <Sparkles className="h-3 w-3" /> Guest Access
          </span>
          <h1 className="mt-4 font-display text-3xl leading-[1.1] tracking-tight text-foreground sm:text-5xl">
            Welcome to Recovery Trauma<span className="text-[var(--gold-deep)]">™</span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-foreground/75 sm:text-base">
            Explore the platform and discover how Recovery Trauma™ supports healing after
            trauma. Take a guided look around before deciding whether Membership
            is right for you.
          </p>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-foreground/60">
            Everything shown here is designed to help you understand what Recovery Trauma™
            offers while protecting premium member content.
          </p>

          <figure className="mx-auto mt-8 max-w-xl rounded-2xl border border-gold/30 bg-card/85 p-5 text-start shadow-sm">
            <figcaption className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--gold-deep)]">
              A note from Jana, founder
            </figcaption>
            <blockquote className="mt-2 text-sm leading-relaxed text-foreground/75">
              “I built Recovery Trauma™ because I live with C-PTSD. After losing my mum as a child,
              I spent years in survival mode, piecing together what actually helped me — grounding,
              journaling, sound and inner child work. This is the gentle, all-in-one place I wish
              I'd had. Everything here comes from lived experience, not a textbook.”
            </blockquote>
          </figure>
        </header>


        {/* Guided journey through the membership */}
        <section className="mt-10 sm:mt-14">
          <ExperienceJourney />
        </section>

        {/* Open features */}
        <section id="explore" className="mt-16 scroll-mt-24">
          <div className="mb-6 text-center sm:mb-8">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--gold-deep)]">
              Open to everyone
            </p>
            <h2 className="mt-2 font-display text-2xl text-foreground sm:text-3xl">
              What's yours to explore today
            </h2>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3">
            {OPEN_FEATURES.map((f) => (
              <FeatureCard
                key={f.title}
                variant="open"
                emoji={f.emoji}
                title={f.title}
                description={f.description}
                to={f.to}
                tone={f.tone}

              />
            ))}
          </div>
        </section>

        {/* Guided grounding video — free for everyone */}
        <section className="mt-16">
          <div className="mb-6 text-center sm:mb-8">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--gold-deep)]">
              Try it now
            </p>
            <h2 className="mt-2 font-display text-2xl text-foreground sm:text-3xl">
              A guided grounding video
            </h2>
            <p className="mx-auto mt-2 max-w-xl text-sm text-foreground/65">
              Press play whenever you're ready — there's no sign-up needed for this one.
            </p>
          </div>

          <GroundingVideo />
        </section>



        {/* Locked features */}
        <section className="mt-16">
          <div className="mb-6 text-center sm:mb-8">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--gold-deep)]">
              Inside Membership
            </p>
            <h2 className="mt-2 font-display text-2xl text-foreground sm:text-3xl">
              A preview of what members unlock
            </h2>
            <p className="mx-auto mt-2 max-w-xl text-sm text-foreground/65">
              These tools live inside the members-only space. Tap any card to see what's
              included.
            </p>
          </div>

          <LockedFeaturesCarousel onOpenLock={openLock} />
        </section>

        {/* Final CTA */}
        <section className="mt-16">
          <div className="relative overflow-hidden rounded-3xl border border-gold/40 bg-gradient-to-br from-[var(--gold-deep)]/95 via-[var(--gold-deep)] to-[oklch(0.42_0.11_75)] p-8 text-center shadow-[var(--shadow-gold)] sm:p-12">
            <div
              aria-hidden
              className="pointer-events-none absolute -end-16 -top-16 h-48 w-48 rounded-full bg-white/10 blur-3xl"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute -bottom-16 -start-16 h-48 w-48 rounded-full bg-white/10 blur-3xl"
            />
            <div className="relative">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-white backdrop-blur">
                <Sparkles className="h-3 w-3" /> Founding Cohort
              </span>
              <h2 className="mt-4 font-display text-3xl leading-tight text-white sm:text-4xl">
                Continue Your Recovery Journey
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-white/85 sm:text-base">
                You've explored a preview of Recovery Trauma™. Become a Member
                to unlock over 150+ trauma-informed recovery tools — guided journals,
                breathing exercises, grounding practices, somatic healing, community
                support, therapist resources, events, and much more.
              </p>
              <div className="mt-7 flex justify-center">
                <Link
                  to="/membership"
                  className="group relative inline-flex items-center gap-2 overflow-hidden rounded-full bg-white px-7 py-3 text-sm font-semibold text-[var(--gold-deep)] dark:bg-card shadow-[0_15px_35px_-10px_rgba(0,0,0,0.35)] transition-all duration-300 hover:-translate-y-0.5 sm:text-base"
                >
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-[var(--gold-deep)]/20 to-transparent transition-transform duration-700 group-hover:translate-x-full"
                  />
                  <Sparkles className="h-4 w-4" />
                  Become a Member
                </Link>
              </div>
              </div>
              <CreatorNote onDark />
            </div>
        </section>





        <section className="mt-16">
          <TestimonialsSection />
        </section>
      </div>

      <LandingFooter />

      <GuestUpgradeModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        featureLabel={featureLabel}
      />
    </div>
  );
}

function LockedFeaturesCarousel({
  onOpenLock,
}: {
  onOpenLock: (label: string) => void;
}) {
  const [index, setIndex] = useState(0);
  const count = LOCKED_FEATURES.length;

  const advance = (direction: 1 | -1) => {
    setIndex((i) => (i + direction + count) % count);
  };

  const current = LOCKED_FEATURES[index];
  const next = LOCKED_FEATURES[(index + 1) % count];

  return (
    <div>
      <div className="relative mx-auto h-[380px] w-full max-w-sm sm:h-[420px]">
        <div
          aria-hidden
          className="assess-card assess-teal absolute inset-0 translate-y-3 scale-[0.96] rounded-[32px] shadow-sm"
        />
        <div
          aria-hidden
          className="assess-card assess-peach absolute inset-0 translate-y-1.5 scale-[0.98] rounded-[32px] shadow-sm"
        />

        <SwipeableLockedCard
          key={`${current.title}-${index}`}
          feature={current}
          nextFeature={next}
          onSwiped={advance}
          onOpen={() => onOpenLock(current.title)}
        />
      </div>

      <div className="mt-5 flex items-center justify-center gap-3">
        <button
          type="button"
          onClick={() => advance(-1)}
          aria-label="Previous feature"
          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-gold/40 bg-card text-foreground shadow-sm transition hover:bg-cream"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-foreground/55">
          {index + 1} <span className="mx-1 text-foreground/30">/</span> {count}
        </p>
        <button
          type="button"
          onClick={() => advance(1)}
          aria-label="Next feature"
          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-gold/40 bg-card text-foreground shadow-sm transition hover:bg-cream"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <p className="mt-2 text-center text-[11px] uppercase tracking-[0.18em] text-foreground/45">
        Drag or tap the arrows
      </p>
    </div>
  );
}

function SwipeableLockedCard({
  feature,
  onSwiped,
  onOpen,
}: {
  feature: LockedFeature;
  nextFeature: LockedFeature;
  onSwiped: (direction: 1 | -1) => void;
  onOpen: () => void;
}) {
  const [drag, setDrag] = useState({ x: 0, y: 0, dragging: false });
  const [leaving, setLeaving] = useState<null | "left" | "right">(null);
  const [entered, setEntered] = useState(false);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const movedRef = useRef(false);
  const elRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setDrag({ x: 0, y: 0, dragging: false });
    setLeaving(null);
    setEntered(false);
    const t = window.setTimeout(() => setEntered(true), 20);
    return () => window.clearTimeout(t);
  }, [feature]);

  const onPointerDown = (e: React.PointerEvent) => {
    if ((e.target as HTMLElement).closest("button, a")) return;
    elRef.current?.setPointerCapture(e.pointerId);
    startRef.current = { x: e.clientX, y: e.clientY };
    movedRef.current = false;
    setDrag((d) => ({ ...d, dragging: true }));
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!startRef.current) return;
    const dx = e.clientX - startRef.current.x;
    const dy = e.clientY - startRef.current.y;
    if (Math.abs(dx) > 6 || Math.abs(dy) > 6) movedRef.current = true;
    setDrag({ x: dx, y: dy * 0.25, dragging: true });
  };
  const endDrag = () => {
    if (!startRef.current) return;
    const { x } = drag;
    startRef.current = null;
    const threshold = 100;
    if (Math.abs(x) > threshold) {
      const dir: "left" | "right" = x > 0 ? "right" : "left";
      setLeaving(dir);
      window.setTimeout(() => onSwiped(dir === "left" ? -1 : 1), 340);
    } else {
      setDrag({ x: 0, y: 0, dragging: false });
    }
  };

  const rotate = drag.x * 0.04;
  const flyX = leaving === "right" ? 700 : leaving === "left" ? -700 : drag.x;
  const flyRot = leaving ? (leaving === "right" ? 18 : -18) : rotate;
  const transform = !entered
    ? "translateY(14px) scale(0.98)"
    : `translate(${flyX}px, ${drag.y}px) rotate(${flyRot}deg)`;
  const transition = drag.dragging
    ? "none"
    : "transform 420ms cubic-bezier(0.22, 1, 0.36, 1), opacity 420ms ease";
  const opacity = leaving ? 0 : entered ? 1 : 0;

  return (
    <div
      ref={elRef}
      role="group"
      aria-label={`${feature.title}. Drag left or right for the next feature. Tap to learn more.`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onClick={() => {
        if (!movedRef.current) onOpen();
      }}
      className="assess-card assess-lavender absolute inset-0 z-10 flex touch-none select-none flex-col items-center justify-between overflow-hidden rounded-[32px] p-8 text-center sm:p-10"
      style={{
        boxShadow:
          "0 30px 70px -20px rgba(184,134,11,0.25), 0 8px 20px -8px rgba(61,43,31,0.10)",
        transform,
        transition,
        opacity,
        cursor: drag.dragging ? "grabbing" : "grab",
      }}
    >
      <div className="relative z-10 inline-flex items-center gap-1.5 rounded-full bg-[var(--gradient-gold)] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-foreground shadow-[var(--shadow-gold)]">
        <Lock className="h-3 w-3" /> Members
      </div>

      <div className="relative z-10 flex flex-col items-center gap-4">
        <div
          aria-hidden
          className="relative flex h-32 w-32 items-center justify-center rounded-3xl bg-[radial-gradient(ellipse_at_center,_var(--cream)_0%,_color-mix(in_oklab,var(--gold-light)_55%,var(--cream))_75%,_transparent_100%)] ring-1 ring-gold-light/50 sm:h-36 sm:w-36"
        >
          <img
            src={feature.art}
            alt=""
            loading="lazy"
            width={640}
            height={640}
            draggable={false}
            className="h-full w-full object-contain p-2"
          />
        </div>
        <h3 className="font-display text-2xl leading-tight text-foreground sm:text-3xl">
          {feature.title}
        </h3>
        <p className="max-w-xs text-sm leading-relaxed text-foreground/70">
          {feature.description}
        </p>
      </div>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpen();
        }}
        className="relative z-10 inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border border-gold/50 bg-background/80 px-4 py-2 text-xs font-semibold text-[var(--gold-deep)] shadow-sm backdrop-blur transition hover:bg-cream"
      >
        See what's included
      </button>
    </div>
  );
}
